import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import StoricoVerificheIntegrazione, { VerificaIntegrazione } from '../../components/StoricoVerificheIntegrazione';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { API_DOCS } from './IntegrazioneCentralinoPage.api';

interface ConfigurazioneCentralino {
  id: number;
  societaId: number;
  urlBase: string;
  tenantSlug: string | null;
  attivo: boolean;
  note: string | null;
}

interface DatiCentralino {
  configurazione: ConfigurazioneCentralino | null;
  verifiche: VerificaIntegrazione[];
  urlPredefinito: string;
}

interface EsitoVerifica {
  esitoOk: boolean;
  messaggio: string;
  durataMs: number;
}

interface Form {
  urlBase: string;
  tenantSlug: string;
  attivo: boolean;
  note: string;
}

/**
 * Collegamento al centralino PianiVoice, uno per società. Oggi PianiVoice non
 * ha credenziali per i gestionali: si registrano indirizzo e tenant e si
 * verifica che il centralino risponda.
 */
export default function IntegrazioneCentralinoPage() {
  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();
  const { can } = usePermesso();
  const puoModificare = can('impostazioni.integrazione_centralino', 'edit');

  const [dati, setDati] = useState<DatiCentralino | null>(null);
  const [form, setForm] = useState<Form>({ urlBase: '', tenantSlug: '', attivo: true, note: '' });
  const [caricamento, setCaricamento] = useState(false);
  const [salvataggio, setSalvataggio] = useState(false);
  const [verificaInCorso, setVerificaInCorso] = useState(false);
  const [errore, setErrore] = useState('');
  const [messaggio, setMessaggio] = useState('');
  const [esito, setEsito] = useState<EsitoVerifica | null>(null);

  const carica = useCallback(async () => {
    if (!pronta || !societaId) {
      setDati(null);
      return;
    }
    try {
      setCaricamento(true);
      setErrore('');
      // @api GET /api/impostazioni/centralino/configurazione?societaId=N -> { success: true, data: { configurazione: ConfigurazioneCentralino | null, verifiche: VerificaIntegrazione[], urlPredefinito } }
      const resp = await apiFetch<{ success: boolean; data: DatiCentralino | null }>(
        `/api/impostazioni/centralino/configurazione?societaId=${societaId}`
      );
      setDati(resp.data);
      const c = resp.data?.configurazione;
      setForm({
        urlBase: c?.urlBase || resp.data?.urlPredefinito || '',
        tenantSlug: c?.tenantSlug || '',
        attivo: c ? c.attivo : true,
        note: c?.note || '',
      });
    } catch (err: any) {
      setErrore(err?.message || 'Errore nel caricamento della configurazione');
    } finally {
      setCaricamento(false);
    }
  }, [pronta, societaId]);

  useEffect(() => {
    setEsito(null);
    setMessaggio('');
    carica();
  }, [carica]);

  const salva = async () => {
    try {
      setSalvataggio(true);
      setErrore('');
      setMessaggio('');
      // @api PUT /api/impostazioni/centralino/configurazione -> { success: true, data: ConfigurazioneCentralino }
      await apiFetch('/api/impostazioni/centralino/configurazione', {
        method: 'PUT',
        body: JSON.stringify({ societaId: Number(societaId), ...form }),
      });
      setMessaggio('Configurazione salvata');
      carica();
    } catch (err: any) {
      setErrore(err?.message || 'Errore nel salvataggio');
    } finally {
      setSalvataggio(false);
    }
  };

  const verifica = async () => {
    try {
      setVerificaInCorso(true);
      setEsito(null);
      // @api POST /api/impostazioni/centralino/verifica -> { success: true, data: { esitoOk, messaggio, durataMs } }
      const resp = await apiFetch<{ success: boolean; data: EsitoVerifica }>('/api/impostazioni/centralino/verifica', {
        method: 'POST',
        body: JSON.stringify({ societaId: Number(societaId) }),
      });
      setEsito(resp.data);
      carica();
    } catch (err: any) {
      setEsito({ esitoOk: false, messaggio: err?.message || 'Errore nella verifica', durataMs: 0 });
    } finally {
      setVerificaInCorso(false);
    }
  };

  const aggiorna = <K extends keyof Form>(campo: K, valore: Form[K]) => setForm(f => ({ ...f, [campo]: valore }));

  return (
    <Layout>
      <div className="bop-page-header">
        <div>
          <h1 className="bop-page-title">Centralino</h1>
          <p className="bop-text-muted">
            Collegamento al centralino PianiVoice della società. Oggi BOP ne verifica la raggiungibilità;
            chiamate e scheda cliente arriveranno quando PianiVoice aprirà la sua API ai gestionali.
          </p>
        </div>
      </div>

      {!isUnica && (
        <div className="bop-filter-bar">
          <div className="bop-field">
            <label className="bop-field__label">Società</label>
            <select className="bop-field__select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
              <option value="">Seleziona società...</option>
              {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>
        </div>
      )}

      {errore && <div className="bop-alert bop-alert--danger">{errore}</div>}

      {!societaId ? (
        <div className="bop-card">
          <div className="bop-empty-state">
            <p className="bop-empty-state__title">Seleziona una società</p>
            <p>Il collegamento al centralino si configura per ogni società.</p>
          </div>
        </div>
      ) : caricamento && !dati ? (
        <div className="bop-card">
          <div className="bop-empty-state"><p>Caricamento...</p></div>
        </div>
      ) : dati && (
        <>
          <div className="bop-card">
            <div className="bop-card__header">
              <h3 className="bop-card__title">Collegamento</h3>
              {puoModificare && (
                <button
                  type="button"
                  className="bop-btn bop-btn-secondary"
                  onClick={verifica}
                  disabled={verificaInCorso || !dati.configurazione}
                  title={!dati.configurazione ? 'Salva prima la configurazione' : undefined}
                >
                  {verificaInCorso ? 'Verifica in corso...' : 'Verifica ora'}
                </button>
              )}
            </div>
            <div className="bop-card__body">
              <div className="bop-form-row">
                <div className="bop-field">
                  <label className="bop-field__label">Indirizzo del centralino</label>
                  <input
                    className="bop-field__input"
                    value={form.urlBase}
                    onChange={e => aggiorna('urlBase', e.target.value)}
                    placeholder={dati.urlPredefinito}
                    disabled={!puoModificare}
                  />
                  <span className="bop-field__hint">Solo https. La verifica chiama {form.urlBase || dati.urlPredefinito}/api/health</span>
                </div>
                <div className="bop-field">
                  <label className="bop-field__label">Tenant PianiVoice</label>
                  <input
                    className="bop-field__input"
                    value={form.tenantSlug}
                    onChange={e => aggiorna('tenantSlug', e.target.value)}
                    placeholder="es. bonacina"
                    disabled={!puoModificare}
                  />
                  <span className="bop-field__hint">Il tenant della società sul centralino (sottodominio &lt;tenant&gt;.voice.pianinvest.it)</span>
                </div>
              </div>

              <div className="bop-field bop-mt-3">
                <label className="bop-field__label">Note</label>
                <textarea
                  className="bop-field__textarea"
                  rows={2}
                  value={form.note}
                  onChange={e => aggiorna('note', e.target.value)}
                  disabled={!puoModificare}
                />
              </div>

              <label className="bop-integrazione__check bop-mt-3">
                <input
                  type="checkbox"
                  checked={form.attivo}
                  onChange={e => aggiorna('attivo', e.target.checked)}
                  disabled={!puoModificare}
                />
                <span>Collegamento attivo (incluso nella verifica notturna)</span>
              </label>

              {messaggio && <div className="bop-alert bop-alert--success bop-mt-3">{messaggio}</div>}
              {esito && (
                <div className={`bop-alert ${esito.esitoOk ? 'bop-alert--success' : 'bop-alert--danger'} bop-mt-3`}>
                  {esito.messaggio}{esito.durataMs > 0 && ` (${esito.durataMs} ms)`}
                </div>
              )}

              {puoModificare && (
                <div className="bop-integrazione__azioni">
                  <button type="button" className="bop-btn bop-btn-primary" onClick={salva} disabled={salvataggio}>
                    {salvataggio ? 'Salvataggio...' : 'Salva'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <StoricoVerificheIntegrazione verifiche={dati.verifiche} />
        </>
      )}

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
